import {FC, ReactNode} from 'react'
import './LiveJobStatusViews.css'

type ShellProps = {
  variant: 'pending' | 'expired' | 'invalid'
  icon: ReactNode
  title: string
  subtitle?: string
  children?: ReactNode
}

/**
 * Full-screen centered status shell for Live Job (pending / expired / invalid).
 */
const LiveJobStatusShell: FC<ShellProps> = ({variant, icon, title, subtitle, children}) => (
  <div className={`live-job-status-root live-job-status-root--${variant}`}>
    <div className='live-job-status-card animate__animated animate__fadeInUp'>
      <div className={`live-job-status-icon-wrap live-job-status-icon-wrap--${variant}`}>{icon}</div>
      <h1 className='live-job-status-title'>{title}</h1>
      {subtitle ? <p className='live-job-status-subtitle'>{subtitle}</p> : null}
      {children}
    </div>
  </div>
)

export const LiveJobPendingView: FC = () => (
  <LiveJobStatusShell
    variant='pending'
    icon={<i className='bi bi-hourglass-split live-job-status-icon' aria-hidden />}
    title='Almost there'
    subtitle='Link is pending. Please wait...'
  >
    <div className='live-job-status-spinner mt-4' role='status' aria-label='Loading'>
      <span className='live-job-status-spinner-dot' />
      <span className='live-job-status-spinner-dot' />
      <span className='live-job-status-spinner-dot' />
    </div>
  </LiveJobStatusShell>
)

export const LiveJobExpiredView: FC = () => (
  <LiveJobStatusShell
    variant='expired'
    icon={<i className='bi bi-clock-history live-job-status-icon' aria-hidden />}
    title='This link has expired'
    subtitle='The tracking link is no longer valid. Request a new link if you still need access.'
  />
)

export const LiveJobInvalidLinkView: FC = () => (
  <LiveJobStatusShell
    variant='invalid'
    icon={<i className='bi bi-shield-exclamation live-job-status-icon' aria-hidden />}
    title='Invalid link'
    subtitle='This URL is not valid or has been revoked. Check the link you were sent and try again.'
  />
)

export const LiveJobLoadingView: FC<{message: string}> = ({message}) => (
  <div className='live-job-status-root live-job-status-root--pending'>
    <div className='live-job-status-card animate__animated animate__fadeIn'>
      <div className='live-job-status-icon-wrap live-job-status-icon-wrap--pending mx-auto'>
        <div className='spinner-border text-primary' role='status' style={{width: '2.5rem', height: '2.5rem'}}>
          <span className='visually-hidden'>Loading</span>
        </div>
      </div>
      <p className='live-job-status-title mb-0 mt-3' style={{fontSize: '1.1rem', fontWeight: 600}}>
        {message}
      </p>
    </div>
  </div>
)

export const LiveJobErrorView: FC<{message: string}> = ({message}) => (
  <div className='live-job-status-root live-job-status-root--invalid'>
    <div className='live-job-status-card animate__animated animate__fadeInUp'>
      <div className='live-job-status-icon-wrap live-job-status-icon-wrap--invalid mx-auto'>
        <i className='bi bi-exclamation-octagon live-job-status-icon' aria-hidden />
      </div>
      <h1 className='live-job-status-title' style={{color: '#f8fafc'}}>
        Something went wrong
      </h1>
      <p className='live-job-status-subtitle' style={{color: '#cbd5e1'}}>
        {message}
      </p>
    </div>
  </div>
)

export const LiveJobNoKeyView: FC = () => (
  <div className='live-job-status-root live-job-status-root--invalid'>
    <div className='live-job-status-card animate__animated animate__fadeInUp'>
      <div className='live-job-status-icon-wrap live-job-status-icon-wrap--invalid mx-auto'>
        <i className='bi bi-link-45deg live-job-status-icon' aria-hidden />
      </div>
      <h1 className='live-job-status-title' style={{color: '#f8fafc'}}>
        Missing link
      </h1>
      <p className='live-job-status-subtitle' style={{color: '#cbd5e1'}}>
        No job key was found in the URL. Open the full tracking link you received.
      </p>
    </div>
  </div>
)
